import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { AuthService } from './auth.service';
import { RoleService } from './role.service';

/**
 * Role Guard - Protects routes based on user roles/groups
 *
 * Usage in routes:
 * {
 *   path: 'admin',
 *   component: AdminComponent,
 *   canActivate: [roleGuard(['Admin', 'SuperAdmin'])]
 * }
 *
 * Or require all roles:
 * {
 *   path: 'super-admin',
 *   component: SuperAdminComponent,
 *   canActivate: [roleGuard(['Admin', 'SuperAdmin'], { requireAll: true })]
 * }
 */
export const roleGuard = (
	requiredRoles: string[],
	options: { requireAll?: boolean; redirectTo?: string } = {}
): CanActivateFn => {
	return async () => {
		const roleService = inject(RoleService);
		const authService = inject(AuthService);
		const router = inject(Router);

		// If no roles required, allow access
		if (!requiredRoles || requiredRoles.length === 0) {
			return true;
		}
		const account = authService.getCurrentUser();
		if (!account) {
			// User not authenticated, deny access
			const redirectTo = options.redirectTo || '/';
			router.navigate([redirectTo]);
			return false;
		}

		// Now check if user has required roles
		const hasAccess = options.requireAll
			? roleService.hasAllRoles(requiredRoles)
			: roleService.hasAnyRole(requiredRoles);

		if (!hasAccess) {
			// Redirect to specified route or home
			const redirectTo = options.redirectTo || '/';
			router.navigate([redirectTo]);
			return false;
		}

		return true;
	};
};
